import { Navigate, useParams } from 'react-router'
import { Trans, useTranslation } from 'react-i18next'
import { useStoreSelector } from 'hooks/store.ts'
import { isActiveContract, isCloseToTermination, isTerminatedContractPastEndDate } from 'utils/contracts.ts'
import useGetAccessRight from 'hooks/useSelectAccessRight.ts'
import { useGetBillShockQuery } from 'store/queries/bolt-backend/billing-contracts'
import UsageCard from '../usage-card/UsageCard.tsx'
import { Banner, Button, Card, Heading, Toggle } from '@boltenergy-be/design-system'
import styles from './CurrentBillingCycle.module.scss'
import CurrentBillingCycleChart from 'components/charts/billing-cycle/current/CurrentBillingCycleChart.tsx'
import { CycleViews } from 'pages/app/billing/billing-cycles/types.ts'
import parse from 'html-react-parser'
import { formatAmount } from 'utils/format.ts'
import { useState } from 'react'
import { getPersistedItem } from 'utils/persistedStorage.ts'
import { HIDE_INACCURACY_WARNING, NEVER_SHOW_INSTALMENT_INFO_MODAL } from 'pages/app/billing/billing-cycles/constants.ts'
import InstalmentInfoModal from 'pages/app/billing/billing-cycles/components/instalment-info-modal/InstalmentInfoModal.tsx'
import useWindowSize from 'hooks/useWindowSize.tsx'
import LoadingSkeleton from 'components/loading-skeleton/LoadingSkeleton.tsx'
import BillshockError from 'pages/app/billing/billing-cycles/components/billshock-error/BillshockError.tsx'
import CustomHeading from '../heading/Heading.tsx'
import OverviewCard from 'pages/app/billing/billing-cycles/components/overview-card/OverviewCard.tsx'
import dayjs from 'dayjs'
import { DATE_FORMAT } from 'constants/constants.ts'
import classNames from 'classnames'
import { routes } from 'types/routes.ts'
import Link from 'components/link/Link.tsx'
import PageLayout from 'layouts/page-layout/PageLayout.tsx'
import { selectCurrentServiceContracts } from 'store/contact/slice.ts'
import { trackEvent } from 'utils/tracking.ts'
import InstalmentCard from 'components/instalment-card/InstalmentCard.tsx'
import EmptyState from 'components/return-later/EmptyState.tsx'
import { TransfoErrorCodes } from 'types/billShock.ts'
import { getSettlementColor } from 'utils/billShock.ts'
import ReturnLater from 'assets/ReturnLater.tsx'
import { ACCESS_RIGHT_KEYS } from 'store/access-rights/constants.ts'
import { FaqSlugB2C } from 'types/faq.ts'
import { hasEnergyTypeValue } from 'store/access-rights/utils.ts'
import { checkHasFixedProduct } from 'utils/products.ts'

const CurrentBillingCycle = () => {
  // Redux
  const { selected, contact } = useStoreSelector((store) => store.contact)
  const { electricity, gas } = useStoreSelector(selectCurrentServiceContracts)
  const serviceContracts = { electricity, gas: isActiveContract(gas) ? gas : undefined }

  // URL Param
  const { id } = useParams<{ id: string }>()

  // i18n
  const { t } = useTranslation(['billing', 'common', 'errors'])

  // Window size
  const { isTablet } = useWindowSize()

  // Access rights
  const billingCyclesAccessRights = useGetAccessRight(ACCESS_RIGHT_KEYS.billingCycles)
  const meterReadingsAccessRights = useGetAccessRight(ACCESS_RIGHT_KEYS.meterReadings)
  const instalmentAccessRights = useGetAccessRight(ACCESS_RIGHT_KEYS.instalment)
  const terminatedContractPastEndDate = isTerminatedContractPastEndDate(serviceContracts.electricity)
  const canAddMeterReadings = hasEnergyTypeValue(meterReadingsAccessRights.canAccess.edit) && meterReadingsAccessRights.showContent
  const canUpdateInstalment = instalmentAccessRights.canAccess && instalmentAccessRights.showContent
  const inaccesible = !selected.billingContract || !billingCyclesAccessRights.showContent.current || terminatedContractPastEndDate
  const inaccuracyReason =
    billingCyclesAccessRights.inaccuracyReason === 'move' ? 'move' : serviceContracts.gas ? 'crisis-bifuel' : 'crisis-el'
  const isCrisisInaccuracy = inaccuracyReason === 'crisis-bifuel' || inaccuracyReason === 'crisis-el'
  const inaccuracySessionKey = inaccuracyReason ? `${HIDE_INACCURACY_WARNING}_${inaccuracyReason}` : HIDE_INACCURACY_WARNING
  const isFixedTariff = checkHasFixedProduct(electricity)

  // Redux queries
  const {
    isFetching,
    isLoading,
    error,
    currentData: data
  } = useGetBillShockQuery(
    {
      billingContractId: selected.billingContract,
      email: contact?.email || '',
      serviceContracts: { electricity, gas }
    },
    { skip: id !== 'current' || inaccesible || !contact }
  )
  const errorCode = typeof error === 'object' ? ('status' in error ? error.status.toString() : error.code) : error

  // Local state
  const [selectedCycleView, setSelectedCycleView] = useState<CycleViews>(CycleViews.CURRENT)
  const [faqOpen, setFaqOpen] = useState<boolean>(false)
  const [openInfoModal, setOpenInfoModal] = useState<boolean>(false)
  const [hasOpenedInfoModal, setHasOpenedInfoModal] = useState<boolean>(!!getPersistedItem(NEVER_SHOW_INSTALMENT_INFO_MODAL))
  const [showInaccuracyWarning, setShowInaccuracyWarning] = useState<boolean>(sessionStorage.getItem(inaccuracySessionKey) !== 'true')

  // Constants - Billshock
  const nearMeterReadingMonth = errorCode?.includes(TransfoErrorCodes.E442_NEAR_METERREADING_MONTH)
  const currentData = selectedCycleView === CycleViews.CURRENT ? data?.billShock : data?.adjustedBillShock || data?.billShock
  const hasAdjustedBillShock = data?.adjustedBillShock
  const currentInstalment = data?.billShock?.instalment?.current || 0
  const proposedInstalment = hasAdjustedBillShock ? 20 : data?.billShock?.instalment?.proposed || 0
  const adjustedBillShockAmount = hasAdjustedBillShock ? data?.adjustedBillShock?.settlement.billShockAmount : undefined
  const settlement = currentData?.settlement.billShockAmount
  const closeToTermination = isCloseToTermination(serviceContracts.electricity)

  // Constants - Instalment
  const totalInstalment = (serviceContracts.electricity?.detail?.instalment || 0) + (serviceContracts.gas?.detail?.instalment || 0)

  // Constants - Last update overview
  const totalPastInstalments = data?.billShock?.past.reduce((sum, instalment) => sum + instalment.billedInstalment, 0)
  const totalFutureInstalments = data?.billShock?.future.reduce((sum, instalment) => sum + instalment.plannedInstalment, 0)

  /**
   * Handles hiding the inaccuracy warning and stores the preference in session storage so it only applies for the current session
   */
  const handleHideInaccuracyWarning = () => {
    sessionStorage.setItem(inaccuracySessionKey, 'true')
    setShowInaccuracyWarning(false)
  }

  return terminatedContractPastEndDate || !billingCyclesAccessRights.canAccess.current ? (
    <Navigate to={terminatedContractPastEndDate ? routes.BILLING_CYCLES : routes.BILLING_INVOICES} replace />
  ) : (
    <PageLayout
      seoTitle={t(($) => $.billingCycle.heading.currentTitle)}
      title={<CustomHeading {...{ isLoading }} currentBillingCycle={currentData} electricityContract={serviceContracts.electricity} />}
      classes={{ body: styles.layout, header: styles.header }}
      withSwitcher
      addressSwitcherClassName={styles.switcher}
      faqCategories={{ b2c: [FaqSlugB2C.BILL_SHOCK] }}
      openFaq={faqOpen}
      onFaqClose={() => setFaqOpen(false)}
    >
      <div className="flex-col gap-500">
        {showInaccuracyWarning && !error && !isLoading && !isFixedTariff && billingCyclesAccessRights.showContent.current && (
          <Card color="soft" className={styles['inaccuracy-card']}>
            <Button
              trailingIcon="cross"
              size="small"
              variant="secondary"
              className={styles.close}
              onClick={() => handleHideInaccuracyWarning()}
            />
            <p className="white-space-pre-line">
              {isCrisisInaccuracy ? (
                <Trans
                  {...{ t }}
                  i18nKey={($) => $.billingCycle[`inaccuracy_${inaccuracyReason}`]}
                  components={{
                    faq: <Button representation="link" variant="inline" onClick={() => setFaqOpen(true)} />
                  }}
                />
              ) : (
                parse(
                  t(($) => $.billingCycle.inaccuracy, {
                    context: inaccuracyReason
                  })
                )
              )}
            </p>
            {!isCrisisInaccuracy && (
              <Button representation="link" onClick={() => setFaqOpen(true)} className={styles['faq-button']}>
                {t(($) => $.billingCycle.faq)}
              </Button>
            )}
          </Card>
        )}

        {instalmentAccessRights.showContent && (
          <div className="flex-col gap-400">
            <InstalmentCard
              {...{ totalInstalment, proposedInstalment }}
              electricityInstalment={serviceContracts.electricity.detail.instalment || 0}
              gasInstalment={serviceContracts.gas?.detail?.instalment}
              estimatedSettlement={data?.billShock?.settlement?.billShockAmount}
              loadingBillShock={isFetching}
            />

            {canUpdateInstalment && (
              <Link
                representation="button"
                loading={isLoading}
                href={routes.BILLING_EDIT_INSTALMENT}
                isFullwidth
                onClick={() =>
                  trackEvent({ category: 'billing', object: 'edit_instalment', action: 'view' }, { from: routes.BILLING_CYCLE_DETAIL })
                }
              >
                {t(($) => $.billingCycle.overview.editInstalment)}
              </Link>
            )}
          </div>
        )}
      </div>

      {!billingCyclesAccessRights.showContent.current ? (
        <section className={styles['return-later']}>
          <ReturnLater className={styles.icon} />

          <Heading as="h2" variant="h6">
            {t(($) => $.billingCycles.returnLater.title)}
          </Heading>

          <p>{parse(t(($) => $.billingCycles.returnLater.paragraph1))}</p>
          <p>{t(($) => $.billingCycles.returnLater.paragraph2)}</p>
        </section>
      ) : nearMeterReadingMonth ? (
        <EmptyState description={t(($) => $.transfo[TransfoErrorCodes.E442_NEAR_METERREADING_MONTH], { ns: 'errors' })} />
      ) : (
        <>
          {/* CHART */}
          <div className="flex-col gap-400">
            <Card as="section" className={styles['chart-card']}>
              {isLoading ? (
                <LoadingSkeleton>
                  <LoadingSkeleton.Rectangle height={56} />
                  <LoadingSkeleton.Rectangle aspectRatio={isTablet ? '1.5 / 1' : '2 / 1'} />
                  <div className="flex-col gap-200">
                    <LoadingSkeleton.Rectangle height={20} />
                    <LoadingSkeleton.Rectangle height={20} />
                  </div>
                </LoadingSkeleton>
              ) : error ? (
                <BillshockError {...{ error }} />
              ) : closeToTermination ? (
                <Banner type="informative">
                  {t(($) => $.closeToTermination, {
                    ns: 'common'
                  })}
                </Banner>
              ) : (
                <>
                  <Heading as="h2" variant="h5" weight="semi-bold">
                    {t(($) => $.billingCycle.chart.currentTitle)}
                  </Heading>

                  {/* CURRENT BILLING CYCLE */}
                  <Toggle
                    optionClassName={styles.option}
                    isFullwidth
                    active={selectedCycleView}
                    onClick={(view) => {
                      if (!hasOpenedInfoModal) {
                        setOpenInfoModal(true)
                      }
                      setSelectedCycleView(view)
                      trackEvent({ category: 'billing', object: 'billing_cycle_view_toggle', action: 'click' }, { view })
                    }}
                    options={[
                      {
                        value: CycleViews.CURRENT,
                        label: parse(
                          t(($) => $.billingCycle.chart.toggle.current, {
                            instalment: formatAmount(currentInstalment)
                          })
                        )
                      },
                      {
                        value: CycleViews.PROPOSED,
                        label: parse(
                          t(($) => $.billingCycle.chart.toggle.proposed, {
                            instalment: formatAmount(proposedInstalment)
                          })
                        )
                      }
                    ]}
                  />

                  {currentData && (
                    <CurrentBillingCycleChart
                      data={currentData}
                      isTablet={isTablet}
                      activePeriod={selectedCycleView}
                      adjustedBillShockAmount={adjustedBillShockAmount}
                    />
                  )}
                </>
              )}

              {/*<Button
          className={styles['consumption-button']}
          onClick={() =>
            navigate({
              pathname: routes.CONSUMPTION_ELECTRICITY, // TODO: determine correct page (gas/el) based on active view
              search: `cycle=${period.id}&type=${getPeriodType(data[period.id])}`
            })
          }
        >
          {t(($) => $.billingCycle.chart.checkPeriodConsumptionBtn)}
        </Button>*/}
            </Card>

            {canUpdateInstalment && (
              <Link
                representation="button"
                loading={isLoading}
                href={routes.BILLING_EDIT_INSTALMENT}
                isFullwidth
                onClick={() =>
                  trackEvent({ category: 'billing', object: 'edit_instalment', action: 'view' }, { from: routes.BILLING_CYCLE_DETAIL })
                }
              >
                {t(($) => $.billingCycle.overview.editInstalment)}
              </Link>
            )}
          </div>

          {/* USAGE CARD*/}
          {(!!data?.billShock || isLoading) && (
            <UsageCard
              title={t(($) => $.billingCycle.usage.title.current)}
              description={t(($) => $.billingCycle.usage.description.current)}
              electricity={data?.billShock?.meters.electricity?.totalConsumption || 0}
              injection={data?.billShock?.meters.electricity?.totalInjection || undefined}
              gas={serviceContracts.gas ? data?.billShock?.meters.gas?.totalConsumption || 0 : undefined}
              canAddMeterReadings={canAddMeterReadings}
              {...{ isLoading }}
            />
          )}

          {(!!data?.billShock || isLoading) && (
            <OverviewCard
              {...{ isLoading }}
              date={dayjs(data?.billShock?.computeDate).format(DATE_FORMAT)}
              blocks={[
                {
                  label: t(($) => $.billingCycle.overview.instalment.current),
                  rawValue: currentInstalment,
                  formattedValue: `€ ${formatAmount(currentInstalment)}`
                },
                {
                  label: t(($) => $.billingCycle.overview.totalBilledInstalments),
                  addendum: data?.billShock
                    ? t(($) => $.billingCycle.overview.instalmentsAddendum, {
                        count: data?.billShock?.past?.length
                      })
                    : undefined,
                  rawValue: totalPastInstalments,
                  formattedValue: typeof totalPastInstalments === 'number' ? `€ ${formatAmount(totalPastInstalments)}` : '—'
                },
                {
                  label: t(($) => $.billingCycle.overview.totalRemainingInstalments),
                  addendum: data?.billShock
                    ? t(($) => $.billingCycle.overview.instalmentsAddendum, {
                        count: data?.billShock?.future?.length
                      })
                    : undefined,
                  rawValue: totalFutureInstalments,
                  formattedValue: typeof totalFutureInstalments === 'number' ? `€ ${formatAmount(totalFutureInstalments)}` : '—'
                },
                {
                  label: t(($) => $.billingCycle.overview.totalInstalments),
                  rawValue: (totalPastInstalments || 0) + (totalFutureInstalments || 0),
                  formattedValue:
                    totalPastInstalments && totalFutureInstalments
                      ? `€ ${formatAmount(totalPastInstalments + totalFutureInstalments)}`
                      : '—'
                },
                {
                  label: t(($) => $.billingCycle.overview.totalCost),
                  rawValue: data?.billShock?.settlement.estimatedTotalAmount,
                  formattedValue: data?.billShock ? `€ ${formatAmount(data?.billShock?.settlement.estimatedTotalAmount)}` : '—'
                },
                {
                  label: t(($) => $.settlement.label),
                  valueClass: classNames(styles['instalment-value'], getSettlementColor(settlement)),
                  rawValue: settlement,
                  formattedValue:
                    typeof settlement === 'number'
                      ? `€ ${formatAmount(Math.abs(settlement))} ${settlement > 0 ? ` ${t(($) => $.settlement.extra)}` : ` ${t(($) => $.settlement.recover)}`}`
                      : '—'
                }
              ]}
            />
          )}
        </>
      )}

      <InstalmentInfoModal
        isOpen={openInfoModal}
        setClose={() => {
          setOpenInfoModal(false)
          setHasOpenedInfoModal(true)
        }}
      />
    </PageLayout>
  )
}

export default CurrentBillingCycle
